import { useState } from 'react';
import { Play, Video } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useLanguage } from '@/contexts/LanguageContext';

const VideoGallery = () => {
  const { t } = useLanguage();
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

  // Real performance videos - using generic thumbnails
  const videos = [
    {
      id: '/videos/video-1.mp4',
      title: 'Performance Video 1',
      description: 'Live performance by Quetschnmanfred',
      thumbnail: '/images/accordion-solo.jpg',
      platform: 'local',
    },
    {
      id: '/videos/video-2.mp4',
      title: 'Performance Video 2',
      description: 'Live performance by Quetschnmanfred',
      thumbnail: '/images/band-stage-blue.jpg',
      platform: 'local',
    },
    {
      id: '/videos/video-3.mp4',
      title: 'Performance Video 3',
      description: 'Live performance by Quetschnmanfred',
      thumbnail: '/images/duo-performance.jpg',
      platform: 'local',
    },
    {
      id: '/videos/video-4.mp4',
      title: 'Performance Video 4',
      description: 'Live performance by Quetschnmanfred',
      thumbnail: '/images/band-stage-red.jpg',
      platform: 'local',
    },
    {
      id: '/videos/video-5.mp4',
      title: 'Performance Video 5',
      description: 'Live performance by Quetschnmanfred',
      thumbnail: '/images/band-wedding.jpg',
      platform: 'local',
    },
  ];

  const openVideo = (videoId: string) => {
    setSelectedVideo(videoId);
  };

  const closeVideo = () => {
    setSelectedVideo(null);
  };

  const getEmbedUrl = (platform: string, id: string) => {
    if (platform === 'youtube') {
      return `https://www.youtube.com/embed/${id}?autoplay=1`;
    }
    // Local video files
    return id;
  };

  return (
    <section id="videos" className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-semibold border border-accent/20">
              <Video className="w-4 h-4" />
              {t.videos.badge}
            </div>
            
            <h2 className="text-4xl md:text-5xl font-serif font-bold">
              {t.videos.title}
              <span className="block text-primary mt-2">{t.videos.titleHighlight}</span>
            </h2>
            
            <p className="text-xl text-foreground/75 max-w-3xl mx-auto leading-relaxed font-medium">
              {t.videos.description}
            </p>
          </div>

          {/* Video Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {videos.map((video, index) => (
              <Card
                key={index}
                className="group hover:shadow-2xl transition-all duration-500 hover:scale-105 border-2 hover:border-primary/50 overflow-hidden cursor-pointer bg-card"
                onClick={() => openVideo(video.id)}
              >
                <CardContent className="p-0 relative">
                  {/* Thumbnail */}
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-colors duration-300 flex items-center justify-center">
                      <div className="bg-accent/90 group-hover:bg-accent rounded-full p-6 group-hover:scale-110 transition-all duration-300 shadow-2xl">
                        <Play className="w-12 h-12 text-white fill-white ml-1" />
                      </div>
                    </div>

                    {/* Duration Badge (Optional) */}
                    <div className="absolute top-4 right-4 bg-black/80 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      5:23
                    </div>
                  </div>

                  {/* Video Info */}
                  <div className="p-6 space-y-2">
                    <h3 className="text-xl font-serif font-bold text-foreground group-hover:text-primary transition-colors">
                      {video.title}
                    </h3>
                    <p className="text-foreground/70 text-sm font-medium">
                      {video.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Bottom Note */}
          <div className="mt-16 text-center">
            <div className="inline-block bg-card border-2 border-primary/30 rounded-3xl p-8 hover:border-primary/60 transition-all hover:shadow-xl">
              <p className="text-lg text-muted-foreground mb-2">
                {t.videos.note}
              </p>
              <p className="text-xl font-semibold">
                {t.videos.noteHighlight}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Video Player Dialog */}
      <Dialog open={selectedVideo !== null} onOpenChange={closeVideo}>
        <DialogContent className="max-w-6xl w-full p-0 bg-black border-none">
          <VisuallyHidden>
            <DialogTitle>Video Player</DialogTitle>
            <DialogDescription>Watch performance videos from Quetschnmanfred</DialogDescription>
          </VisuallyHidden>
          {selectedVideo !== null && (
            <div className="relative aspect-video">
              {selectedVideo.startsWith('/videos/') ? (
                <video
                  src={selectedVideo}
                  controls
                  autoPlay
                  className="w-full h-full"
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <iframe
                  src={getEmbedUrl('youtube', selectedVideo)}
                  title="Video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default VideoGallery;
